import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Home, Loader2, Search, Truck, AlertCircle,
} from 'lucide-react';

const statusBadge = (status) => {
  const map = {
    available: 'bg-green-500/20 text-green-400 hover:bg-green-500/30',
    in_use: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30',
    out_of_service: 'bg-red-500/20 text-red-400 hover:bg-red-500/30',
    maintenance: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30',
  };
  return map[status] || 'bg-gray-500/20 text-[var(--m-text-secondary)] hover:bg-gray-500/30';
};

const MobileEquipment = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [noSupplier, setNoSupplier] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/api/mobile/equipment');
        setItems(res.data.items || []);
        setNoSupplier(!!res.data.no_supplier);
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(e =>
      (e.asset_id || '').toLowerCase().includes(q) ||
      (e.equipment_type || '').toLowerCase().includes(q) ||
      (e.name || '').toLowerCase().includes(q) ||
      (e.capacity || '').toLowerCase().includes(q) ||
      (e.registration || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <MobileLayout>
      <div className="min-h-full bg-[var(--m-bg)]" data-testid="mobile-equipment-list">
        {/* Header */}
        <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-1" data-testid="back-btn">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-[#ffd108]" />
              <h1 className="text-[var(--m-text)] font-semibold">Assets</h1>
            </div>
          </div>
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-1" data-testid="home-btn">
            <Home className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-muted)]" />
            <input
              type="text"
              placeholder="Search assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[var(--m-bg-card)] text-[var(--m-text)] pl-10 pr-4 py-2.5 rounded-lg text-sm placeholder-[var(--m-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#ffd108]/50"
              data-testid="equipment-search"
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          ) : noSupplier ? (
            <div className="text-center py-16" data-testid="no-supplier-state">
              <AlertCircle className="h-12 w-12 text-[var(--m-text-muted)] mx-auto mb-3" />
              <p className="text-[var(--m-text-secondary)] font-medium">No supplier assigned</p>
              <p className="text-[var(--m-text-muted)] text-sm mt-1">Contact your administrator to be assigned to a supplier</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-equipment-state">
              <Truck className="h-12 w-12 text-[var(--m-text-muted)] mx-auto mb-3" />
              <p className="text-[var(--m-text-secondary)]">{search ? 'No assets match your search' : 'No assets found'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-[var(--m-text-muted)] mb-2">{filtered.length} asset{filtered.length !== 1 ? 's' : ''}</p>
              {filtered.map((eq) => (
                <button
                  key={eq.id}
                  onClick={() => navigate(`/mobile/equipment/${eq.id}`)}
                  className="w-full p-3 bg-[var(--m-bg-card)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors flex items-center gap-3"
                  data-testid={`equipment-card-${eq.id}`}
                >
                  {eq.photo_url ? (
                    <img src={eq.photo_url} alt={eq.asset_id} className="w-12 h-12 rounded-lg object-cover bg-black/30 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[var(--m-bg-elevated)] flex items-center justify-center flex-shrink-0">
                      <Truck className="h-5 w-5 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[var(--m-text)] font-medium text-sm truncate">{eq.asset_id || eq.name || 'Unnamed'}</p>
                      <Badge className={`text-[10px] ${statusBadge(eq.status)}`}>
                        {(eq.status || 'unknown').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-[var(--m-text-secondary)] truncate mt-0.5">
                      {[eq.equipment_type, eq.capacity].filter(Boolean).join(' · ') || 'No details'}
                    </p>
                    {eq.registration && (
                      <p className="text-xs text-[var(--m-text-muted)] mt-0.5">Rego: {eq.registration}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileEquipment;
